// Copyright (C) 2021 Intel Corporation
//
// SPDX-License-Identifier: MIT

// AI reference grades of the card on screen (cvat/apps/engine/ai_grades.py). The server keeps
// them by card, for everyone who opens it: the panel reads them, starts the first run of a card
// that has none (administrators and reviewers), runs a picked model on demand, and polls while
// a row of the card is being graded.

import { ActionUnion, createAction, ThunkAction } from 'utils/redux';
import {
    AiGradeProvider, AiGradeResult, AiGradeSource, AiModelPick, AiProviderModels, AiSpend, AiSpendPeriod,
} from 'reducers/interfaces';
import getCore from 'cvat-core-wrapper';
import { AI_GRADE_PROVIDERS, AI_GRADE_SOURCES, toAiGradeResult } from 'utils/ai-grades';

const core = getCore();

const POLL_INTERVAL_MS = 3000;
const POLL_ERRORS_TOLERATED = 5;

export enum AiGradesActionTypes {
    RESET_AI_GRADES = 'grades@RESET_AI_GRADES',
    AI_GRADES_LOADED = 'grades@AI_GRADES_LOADED',
    AI_GRADES_FAILED = 'grades@AI_GRADES_FAILED',
    AI_GRADES_RUN_REQUESTED = 'grades@AI_GRADES_RUN_REQUESTED',
    AI_GRADES_RUN_REQUEST_ENDED = 'grades@AI_GRADES_RUN_REQUEST_ENDED',
    AI_SPEND_LOADED = 'grades@AI_SPEND_LOADED',
    LOAD_AI_MODELS = 'grades@LOAD_AI_MODELS',
    LOAD_AI_MODELS_SUCCESS = 'grades@LOAD_AI_MODELS_SUCCESS',
    LOAD_AI_MODELS_FAILED = 'grades@LOAD_AI_MODELS_FAILED',
    PICK_AI_MODEL = 'grades@PICK_AI_MODEL',
}

// The card's grades as the server answers them
export interface AiCardState {
    results: AiGradeResult[];
    busy: boolean;
    canGenerate: boolean;
    spentUsd: number;
    calls: number;
    unpricedCalls: number;
}

export const aiGradesActions = {
    reset: (certificateId: string | null) => (
        createAction(AiGradesActionTypes.RESET_AI_GRADES, { certificateId })
    ),
    loaded: (certificateId: string, card: AiCardState) => (
        createAction(AiGradesActionTypes.AI_GRADES_LOADED, { certificateId, card })
    ),
    failed: (certificateId: string, error: string) => (
        createAction(AiGradesActionTypes.AI_GRADES_FAILED, { certificateId, error })
    ),
    runRequested: (provider: AiGradeProvider) => (
        createAction(AiGradesActionTypes.AI_GRADES_RUN_REQUESTED, { provider })
    ),
    runRequestEnded: (provider: AiGradeProvider) => (
        createAction(AiGradesActionTypes.AI_GRADES_RUN_REQUEST_ENDED, { provider })
    ),
    spendLoaded: (spend: AiSpend) => createAction(AiGradesActionTypes.AI_SPEND_LOADED, { spend }),
    loadModels: () => createAction(AiGradesActionTypes.LOAD_AI_MODELS),
    loadModelsSuccess: (
        canGenerate: boolean,
        canSeeSpend: boolean,
        autoModels: { provider: AiGradeProvider; model: string }[],
        providers: Record<AiGradeProvider, AiProviderModels>,
        picks: Partial<Record<AiGradeProvider, AiModelPick>>,
    ) => createAction(AiGradesActionTypes.LOAD_AI_MODELS_SUCCESS, {
        canGenerate, canSeeSpend, autoModels, providers, picks,
    }),
    loadModelsFailed: () => createAction(AiGradesActionTypes.LOAD_AI_MODELS_FAILED),
    pickModel: (provider: AiGradeProvider, pick: AiModelPick) => (
        createAction(AiGradesActionTypes.PICK_AI_MODEL, { provider, pick })
    ),
};

export type AiGradesActions = ActionUnion<typeof aiGradesActions>;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => { setTimeout(resolve, ms); });

// cvat-core folds the response into ServerError.message as
// `Request failed with status code 400. <the body as JSON>.`; the panel shows the body's text
function serverMessage(error: any): string {
    let text = `${error?.message ?? error ?? 'Request failed'}`;
    const match = text.match(/^Request failed with status code \d+\. ([\s\S]*)\.$/);
    if (match) {
        try {
            const body = JSON.parse(match[1]);
            text = typeof body === 'string' ? body : `${body?.detail ?? match[1]}`;
        } catch {
            [, text] = match;
        }
    }
    // strip any HTML an error page brought along
    return text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 300);
}

const toCardState = (data: any): AiCardState => ({
    results: (data?.results ?? []).map(toAiGradeResult),
    busy: !!data?.busy,
    canGenerate: !!data?.can_generate,
    spentUsd: Number(data?.spent_usd ?? 0),
    calls: Number(data?.calls ?? 0),
    unpricedCalls: Number(data?.unpriced_calls ?? 0),
});

// A frame of the card on screen: the server finds the card's certificate and scans from it
export interface AiCard {
    jobId: number;
    frame: number;
    certificateId: string;
}

const cardUrl = (card: AiCard): string => `${core.config.backendAPI}/jobs/${card.jobId}/ai-grades`;

// The imaging sources the card on a frame has on both sides; a card without a height map or a
// camera photo gets no rows for it.
export async function fetchAiGradeSources(jobId: number, frame: number): Promise<AiGradeSource[]> {
    const data = await core.server.request(`${core.config.backendAPI}/jobs/${jobId}/ai-grades/sources`, {
        method: 'GET',
        params: { frame },
    });
    return AI_GRADE_SOURCES.filter((source) => (data?.sources ?? []).includes(source));
}

export function loadAiSpendAsync(): ThunkAction {
    return async (dispatch): Promise<void> => {
        try {
            const data = await core.server.request(`${core.config.backendAPI}/ai-grades/spend`, { method: 'GET' });
            const period = (raw: any): AiSpendPeriod => ({
                costUsd: Number(raw?.cost_usd ?? 0),
                calls: Number(raw?.calls ?? 0),
                cards: Number(raw?.cards ?? 0),
                unpricedCalls: Number(raw?.unpriced_calls ?? 0),
            });
            dispatch(aiGradesActions.spendLoaded({
                today: period(data?.today), week: period(data?.week), month: period(data?.month),
            }));
        } catch {
            // the summary keeps its last figures
        }
    };
}

let latestWatch = 0;

// Reads the card's grades, starts the first run of a card that has none when this person may,
// and polls while a row of the card is being graded. A newer watch, another card on screen or
// the panel closing ends it.
export function watchAiGradesAsync(card: AiCard, startFirstRun: boolean): ThunkAction {
    return async (dispatch, getState): Promise<void> => {
        latestWatch += 1;
        const watch = latestWatch;
        const current = (): boolean => (
            watch === latestWatch &&
            getState().annotation.gradesFrom.open &&
            getState().grades.ai.certificateId === card.certificateId
        );

        let firstRun = startFirstRun;
        let sawBusy = false;
        let errors = 0;
        while (current()) {
            let data: any;
            try {
                data = await core.server.request(cardUrl(card), { method: 'GET', params: { frame: card.frame } });
                errors = 0;
            } catch (error) {
                errors += 1;
                if (errors > POLL_ERRORS_TOLERATED) {
                    if (current()) {
                        dispatch(aiGradesActions.failed(card.certificateId, serverMessage(error)));
                    }
                    return;
                }
                await sleep(POLL_INTERVAL_MS);
                continue;
            }

            if (firstRun && data.can_generate && !data.busy && !(data.results ?? []).length && current()) {
                // the server starts it once per card, whoever asks first; both providers' buttons wait
                firstRun = false;
                AI_GRADE_PROVIDERS.forEach((provider) => dispatch(aiGradesActions.runRequested(provider)));
                try {
                    data = await core.server.request(cardUrl(card), {
                        method: 'POST',
                        data: { frame: card.frame, automatic: true },
                    });
                } catch (error) {
                    if (current()) {
                        dispatch(aiGradesActions.failed(card.certificateId, serverMessage(error)));
                    }
                    return;
                } finally {
                    AI_GRADE_PROVIDERS.forEach((provider) => dispatch(aiGradesActions.runRequestEnded(provider)));
                }
            }
            if (!current()) {
                return;
            }

            dispatch(aiGradesActions.loaded(card.certificateId, toCardState(data)));
            if (!data.busy) {
                if (sawBusy && getState().grades.aiModels.canSeeSpend) {
                    dispatch(loadAiSpendAsync());
                }
                return;
            }
            sawBusy = true;
            await sleep(POLL_INTERVAL_MS);
        }
    };
}

// Runs one model on the card: on every source it has, or on the given ones, with this person's
// pick for the provider unless another is given; its rows are replaced as the answers come in.
export function runAiModelAsync(
    card: AiCard,
    provider: AiGradeProvider,
    options: { sources?: AiGradeSource[]; pick?: AiModelPick | null } = {},
): ThunkAction {
    return async (dispatch, getState): Promise<void> => {
        const pick = options.pick ?? getState().grades.aiModels.picks[provider] ?? null;
        dispatch(aiGradesActions.runRequested(provider));
        try {
            const data = await core.server.request(cardUrl(card), {
                method: 'POST',
                data: {
                    frame: card.frame,
                    provider,
                    model: pick?.model,
                    effort: pick?.effort ?? undefined,
                    sources: options.sources,
                },
            });
            if (getState().grades.ai.certificateId === card.certificateId) {
                dispatch(aiGradesActions.loaded(card.certificateId, toCardState(data)));
                dispatch(watchAiGradesAsync(card, false));
            }
        } catch (error) {
            if (getState().grades.ai.certificateId === card.certificateId) {
                dispatch(aiGradesActions.failed(card.certificateId, serverMessage(error)));
            }
        } finally {
            dispatch(aiGradesActions.runRequestEnded(provider));
        }
    };
}

// The model and effort of this person's runs by hand (cvat/apps/engine/ai_models.py): the
// server's defaults and lists, and their own picks, which go with their runs and are remembered
// in their browser. Nothing is stored on the server, so one admin's pick never changes another's.
const AI_MODELS_URL = (): string => `${core.config.backendAPI}/ai-grades/models`;
const PICKS_STORAGE_KEY = 'ags.aiGrades.picks';

const defaultEffortOf = (efforts: string[]): string | null => {
    if (!efforts.length) return null;
    return efforts.includes('high') ? 'high' : efforts[efforts.length - 1];
};

function readStoredPicks(): Partial<Record<AiGradeProvider, AiModelPick>> {
    try {
        return JSON.parse(localStorage.getItem(PICKS_STORAGE_KEY) || '{}') ?? {};
    } catch {
        return {};
    }
}

function storePicks(picks: Partial<Record<AiGradeProvider, AiModelPick>>): void {
    try {
        localStorage.setItem(PICKS_STORAGE_KEY, JSON.stringify(picks));
    } catch {
        // private mode or storage disabled: the pick lasts until the page reloads
    }
}

// a pick the provider still offers, otherwise the default
function validPick(entry: AiProviderModels, pick: Partial<AiModelPick> | undefined): AiModelPick {
    const model = entry.models.find((option) => option.id === pick?.model);
    if (!model) {
        return { model: entry.defaultModel, effort: entry.defaultEffort };
    }
    const effort = pick?.effort && model.efforts.includes(pick.effort) ? pick.effort : defaultEffortOf(model.efforts);
    return { model: model.id, effort };
}

export function loadAiModelsAsync(): ThunkAction {
    return async (dispatch): Promise<void> => {
        dispatch(aiGradesActions.loadModels());
        try {
            const data = await core.server.request(AI_MODELS_URL(), { method: 'GET' });
            const providers = Object.fromEntries(AI_GRADE_PROVIDERS.map((provider) => {
                const raw = data.providers?.[provider] ?? {};
                return [provider, {
                    defaultModel: raw.default ?? '',
                    defaultEffort: raw.default_effort ?? null,
                    models: (raw.models ?? []).map((model: any) => ({
                        id: model.id, name: model.name ?? model.id, efforts: model.efforts ?? [],
                    })),
                    error: raw.error ?? null,
                }];
            })) as Record<AiGradeProvider, AiProviderModels>;
            const stored = readStoredPicks();
            const picks = Object.fromEntries(AI_GRADE_PROVIDERS
                .filter((provider) => !providers[provider].error)
                .map((provider) => [provider, validPick(providers[provider], stored[provider])]));
            const autoModels = (data.auto_models ?? []).map((entry: any) => ({
                provider: entry.provider, model: entry.model,
            }));
            dispatch(aiGradesActions.loadModelsSuccess(
                !!data.can_generate, !!data.can_see_spend, autoModels, providers, picks,
            ));
            if (data.can_see_spend) {
                dispatch(loadAiSpendAsync());
            }
        } catch {
            // the pickers say the models could not be loaded and generating waits for them
            dispatch(aiGradesActions.loadModelsFailed());
        }
    };
}

export function pickAiModel(
    provider: AiGradeProvider,
    change: { model: string } | { effort: string },
): ThunkAction {
    return async (dispatch, getState): Promise<void> => {
        const { aiModels } = getState().grades;
        const entry = aiModels.providers?.[provider];
        if (!entry) {
            return;
        }
        const current = aiModels.picks[provider] ?? { model: entry.defaultModel, effort: entry.defaultEffort };
        // a new model keeps the effort if it takes it, and otherwise starts at its default
        const pick = validPick(entry, { ...current, ...change });
        dispatch(aiGradesActions.pickModel(provider, pick));
        storePicks({ ...readStoredPicks(), [provider]: pick });
    };
}
